import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Plus, Trash2, Calculator, RefreshCw } from "lucide-react";

export default function OrderFormMultiPart({ user, onOrderCreated }) {
  const [jobNumber, setJobNumber] = useState("");
  const [assignedTechName, setAssignedTechName] = useState("");
  const [shippingDestination, setShippingDestination] = useState("Tech");
  const [notes, setNotes] = useState("");
  const [techs, setTechs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [needsReplacement, setNeedsReplacement] = useState(false);
  const [partSources, setPartSources] = useState([]);
  
  // Parts list
  const [parts, setParts] = useState([]);
  
  // Current part being added
  const [currentPart, setCurrentPart] = useState({
    part_number: "",
    part_source: "",
    brand: "",
    quantity: 1,
    part_cost: ""
  });
  const [customBrand, setCustomBrand] = useState("");
  const [brands, setBrands] = useState([]);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [truckHint, setTruckHint] = useState(null); // {found, item} — "You have this in your truck"

  // Check if current source is truck part
  const isStockSource = currentPart.part_source === "Truck Part";

  useEffect(() => {
    fetchPartSources();
    fetchBrands();
    if (user.role === "admin" || user.role === "part_manager") {
      fetchTechs();
    }
  }, [user.role]);

  const fetchBrands = async () => {
    try {
      const res = await axios.get("/brands");
      setBrands(res.data?.brands || []);
    } catch {
      setBrands(["Whirlpool","Samsung","LG","GE","Frigidaire","Maytag","Bosch","KitchenAid","Kenmore","Other"]);
    }
  };

  // Debounced check: does the tech already have this part in their truck?
  useEffect(() => {
    const pn = (currentPart.part_number || "").trim();
    if (!pn) { setTruckHint(null); return; }
    // Skip when the tech is already ordering FROM the truck
    if (currentPart.part_source === "Truck Part") { setTruckHint(null); return; }

    // Which tech's truck to check
    let targetTechId = user?.id;
    if ((user.role === "admin" || user.role === "part_manager") && assignedTechName) {
      const t = techs.find((x) => x.full_name === assignedTechName);
      targetTechId = t?.id;
    }
    if (!targetTechId) { setTruckHint(null); return; }

    const t = setTimeout(async () => {
      try {
        const res = await axios.get("/truck-inventory/lookup", {
          params: { part_number: pn, tech_id: targetTechId },
        });
        if (res.data?.found && (res.data.item?.quantity || 0) > 0) {
          setTruckHint(res.data);
        } else {
          setTruckHint(null);
        }
      } catch { setTruckHint(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [currentPart.part_number, currentPart.part_source, assignedTechName, user?.id, techs, user?.role]);

  // Reset needsReplacement when source changes away from Stock/Truck Part
  useEffect(() => {
    if (!isStockSource) {
      setNeedsReplacement(false);
    }
  }, [currentPart.part_source, isStockSource]);

  const fetchPartSources = async () => {
    try {
      const response = await axios.get("/settings/part-sources");
      const enabledSources = (response.data.sources || []).filter(s => s.enabled);
      setPartSources(enabledSources);
      // Set default part source to first enabled one
      if (enabledSources.length > 0 && !currentPart.part_source) {
        setCurrentPart(prev => ({ ...prev, part_source: enabledSources[0].name }));
      }
    } catch (error) {
      console.error("Failed to fetch part sources:", error);
      // Fallback to default sources
      const defaultSources = [
        { id: "amazon", name: "Amazon", enabled: true },
        { id: "marcone", name: "Marcone", enabled: true },
        { id: "truck_part", name: "Truck Part", enabled: true },
      ];
      setPartSources(defaultSources);
      setCurrentPart(prev => ({ ...prev, part_source: "Amazon" }));
    }
  };

  const fetchTechs = async () => {
    try {
      const response = await axios.get("/auth/techs");
      setTechs(response.data);
    } catch (error) {
      console.error("Failed to fetch techs:", error);
    }
  };

  // Calculate markup
  const calculateMarkup = (cost, source, qty) => {
    const partCost = parseFloat(cost) || 0;
    const quantity = parseInt(qty) || 1;
    let shipping = 0;
    let taxAmount = 0;
    let subtotal = partCost;
    let markupPercentage = 0;
    
    if (source === "Marcone") {
      // Marcone: Cost + $12 shipping + 8% tax, NO markup
      shipping = 12;
      const subtotalBeforeTax = partCost + shipping;
      taxAmount = subtotalBeforeTax * 0.08;
      subtotal = subtotalBeforeTax + taxAmount;
    } else if (source === "Tech Purchase") {
      // Tech Purchase: Keep as-is, no changes
      subtotal = partCost;
    } else {
      // Amazon, Stock, Truck Part, Others: Markup first, then 8% tax on original cost
      
      // Step 1: Markup tiers based on ORIGINAL cost
      if (partCost < 25) markupPercentage = 150;
      else if (partCost <= 50) markupPercentage = 100;
      else if (partCost <= 100) markupPercentage = 50;
      else if (partCost <= 200) markupPercentage = 35;
      else markupPercentage = 25;
      
      // Step 2: Calculate markup on original cost
      const markupAmount = partCost * (markupPercentage / 100);
      
      // Step 3: Add 8% tax on ORIGINAL cost only
      taxAmount = partCost * 0.08;
      
      // Step 4: Final = original + markup + tax
      subtotal = partCost + markupAmount + taxAmount;
    }
    
    const totalPerUnit = Math.max(subtotal, 25);
    return {
      price: parseFloat((totalPerUnit * quantity).toFixed(2)),
      breakdown: { shipping, tax: taxAmount, markup: markupPercentage }
    };
  };

  const handleCalculate = () => {
    if (!currentPart.part_cost || parseFloat(currentPart.part_cost) <= 0) {
      toast.error("Enter a valid part cost");
      return;
    }
    const result = calculateMarkup(currentPart.part_cost, currentPart.part_source, currentPart.quantity);
    setCalculatedPrice(result.price);
  };

  const addPart = () => {
    if (!currentPart.part_number) {
      toast.error("Enter part number");
      return;
    }
    const effectiveBrand = currentPart.brand === "Other"
      ? customBrand.trim()
      : (currentPart.brand || "").trim();
    if (!effectiveBrand) {
      toast.error("Pick a brand");
      return;
    }
    if (!calculatedPrice) {
      toast.error("Calculate price first");
      return;
    }
    
    const isStockPart = currentPart.part_source === "Truck Part";
    
    setParts([...parts, {
      part_number: currentPart.part_number,
      part_source: currentPart.part_source,
      brand: effectiveBrand,
      quantity: parseInt(currentPart.quantity) || 1,
      part_cost: parseFloat(currentPart.part_cost),
      calculated_price: calculatedPrice,
      needs_replacement: isStockPart ? needsReplacement : null
    }]);
    
    // Reset current part
    setCurrentPart({
      part_number: "",
      part_source: "Amazon",
      brand: "",
      quantity: 1,
      part_cost: ""
    });
    setCustomBrand("");
    setCalculatedPrice(null);
    setNeedsReplacement(false);
    toast.success("Part added!");
  };

  const removePart = (index) => {
    setParts(parts.filter((_, i) => i !== index));
  };

  const getTotal = () => {
    return parts.reduce((sum, p) => sum + p.calculated_price, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!jobNumber) {
      toast.error("Enter job number");
      return;
    }
    
    if (parts.length === 0) {
      toast.error("Add at least one part");
      return;
    }

    if ((user.role === "admin" || user.role === "part_manager") && !assignedTechName) {
      toast.error("Select a technician");
      return;
    }

    setLoading(true);
    try {
      // Validate every added part has a brand — surface a clear message BEFORE hitting the API.
      const missingBrandIdx = parts.findIndex((p) => !(p.brand || "").trim());
      if (missingBrandIdx >= 0) {
        toast.error(`Part #${missingBrandIdx + 1} is missing a brand. Remove and re-add it.`);
        setLoading(false);
        return;
      }
      // Create separate order for each part
      const orderPromises = parts.map((part, index) => {
        // Check if this part is Truck Part
        const isStockPart = part.part_source === "Truck Part";
        
        return axios.post("/orders", {
          job_number: jobNumber,
          part_number: part.part_number,
          part_source: part.part_source,
          brand: part.brand,
          quantity: part.quantity,
          part_cost: part.part_cost,
          calculated_price: part.calculated_price,
          assigned_tech_name: assignedTechName || user.full_name,
          shipping_destination: shippingDestination,
          notes: index === 0 ? notes : null,
          needs_replacement: isStockPart ? part.needs_replacement : null
        });
      });
      
      await Promise.all(orderPromises);
      
      toast.success(`${parts.length} order${parts.length > 1 ? 's' : ''} created for Job #${jobNumber}!`);
      
      // Reset form
      setJobNumber("");
      setAssignedTechName("");
      setShippingDestination("Tech");
      setNotes("");
      setParts([]);
      setNeedsReplacement(false);
      setCurrentPart({
        part_number: "",
        part_source: "Amazon",
        quantity: 1,
        part_cost: ""
      });
      setCalculatedPrice(null);
      
      onOrderCreated();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create orders");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
      {/* Job Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Job Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="job_number">Job Number *</Label>
              <Input
                id="job_number"
                value={jobNumber}
                onChange={(e) => setJobNumber(e.target.value)}
                placeholder="Enter job number"
                required
              />
            </div>
            {(user.role === "admin" || user.role === "part_manager") && (
              <div>
                <Label>Assign Tech *</Label>
                <select
                  value={assignedTechName}
                  onChange={(e) => setAssignedTechName(e.target.value)}
                  className="w-full h-10 px-3 border border-slate-300 rounded-md"
                  required
                >
                  <option value="">Select Tech</option>
                  {techs.map((tech) => (
                    <option key={tech.id} value={tech.full_name}>
                      {tech.full_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ship To</Label>
              <select
                value={shippingDestination}
                onChange={(e) => setShippingDestination(e.target.value)}
                className="w-full h-10 px-3 border border-slate-300 rounded-md"
              >
                <option value="Tech">Tech</option>
                <option value="Office">Office</option>
                <option value="Customer">Customer</option>
              </select>
            </div>
            <div>
              <Label>Notes</Label>
              <Input
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Optional notes"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Add Part Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Add Part
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {truckHint?.found && (
            <div
              className="rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-start gap-3"
              data-testid="truck-hint-banner"
            >
              <div className="flex-1">
                <p className="font-medium">
                  {truckHint.item.tech_name} has {truckHint.item.quantity}× of{" "}
                  <span className="font-mono">{truckHint.item.part_number}</span> in truck
                </p>
                <p className="text-xs text-amber-800/80">
                  You can pull from stock instead of ordering a new one.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="border-amber-400 text-amber-900 hover:bg-amber-100 h-7"
                onClick={() => {
                  const idx = partSources.findIndex((s) => s.name === "Truck Part");
                  if (idx >= 0) setCurrentPart({ ...currentPart, part_source: "Truck Part" });
                }}
                data-testid="truck-hint-use-btn"
              >
                Use from truck
              </Button>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div>
              <Label>Part Number *</Label>
              <Input
                value={currentPart.part_number}
                onChange={(e) => setCurrentPart({...currentPart, part_number: e.target.value})}
                placeholder="e.g. W10846539"
                data-testid="order-part-number"
              />
            </div>
            <div>
              <Label>Brand *</Label>
              <select
                value={currentPart.brand}
                onChange={(e) => setCurrentPart({...currentPart, brand: e.target.value})}
                className="w-full h-10 px-3 border border-slate-300 rounded-md"
                data-testid="order-brand"
              >
                <option value="">Pick brand...</option>
                {brands.map(b => (<option key={b} value={b}>{b}</option>))}
              </select>
              {currentPart.brand === "Other" && (
                <Input
                  value={customBrand}
                  onChange={(e) => setCustomBrand(e.target.value)}
                  placeholder="Custom brand"
                  className="mt-1"
                  data-testid="order-brand-custom"
                />
              )}
            </div>
            {/* Only show Source dropdown if there are enabled sources */}
            {partSources.length > 0 && (
              <div>
                <Label>Source</Label>
                <select
                  value={currentPart.part_source}
                  onChange={(e) => {
                    setCurrentPart({...currentPart, part_source: e.target.value});
                    setCalculatedPrice(null);
                  }}
                  className="w-full h-10 px-3 border border-slate-300 rounded-md"
                >
                  {partSources.map(source => (
                    <option key={source.id} value={source.name}>{source.name}</option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <Label>Quantity</Label>
              <Input
                type="number"
                min="1"
                value={currentPart.quantity}
                onChange={(e) => {
                  setCurrentPart({...currentPart, quantity: e.target.value});
                  setCalculatedPrice(null);
                }}
              />
            </div>
            <div>
              <Label>Part Cost ($) *</Label>
              <Input
                type="number"
                step="0.01"
                value={currentPart.part_cost}
                onChange={(e) => {
                  setCurrentPart({...currentPart, part_cost: e.target.value});
                  setCalculatedPrice(null);
                }}
                placeholder="0.00"
              />
            </div>
          </div>
          
          {/* Stock Replacement Option - Only show for Stock/Truck Part */}
          {isStockSource && (
            <div className="flex items-center gap-3 p-3 bg-orange-50 border border-orange-200 rounded-lg">
              <input
                type="checkbox"
                id="needsReplacement"
                checked={needsReplacement}
                onChange={(e) => setNeedsReplacement(e.target.checked)}
                className="w-5 h-5 text-orange-600 rounded"
              />
              <label htmlFor="needsReplacement" className="flex items-center gap-2 cursor-pointer">
                <RefreshCw className="w-4 h-4 text-orange-600" />
                <span className="font-medium text-orange-800">Order replacement for stock</span>
              </label>
              <span className="text-xs text-orange-600 ml-auto">
                {needsReplacement ? "Dispatch will order" : "No restock needed"}
              </span>
            </div>
          )}
          
          <div className="flex items-center gap-4">
            <Button type="button" variant="outline" onClick={handleCalculate}>
              <Calculator className="w-4 h-4 mr-2" />
              Calculate Price
            </Button>
            
            {calculatedPrice && (
              <div className="flex items-center gap-4">
                <span className="text-lg font-bold text-green-600">
                  Price: ${calculatedPrice.toFixed(2)}
                </span>
                <Button type="button" onClick={addPart}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add to Order
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Parts Added */}
      {parts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Parts to Order ({parts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {parts.map((part, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-4">
                    <span className="w-6 h-6 bg-slate-300 rounded-full flex items-center justify-center text-xs font-bold">
                      {index + 1}
                    </span>
                    <div>
                      <span className="font-medium">{part.part_number}</span>
                      <span className="text-slate-500 ml-2">• {part.brand || "—"}</span>
                      <span className="text-slate-500 ml-2">• {part.part_source}</span>
                      <span className="text-slate-500 ml-2">• Qty: {part.quantity}</span>
                      {part.needs_replacement && (
                        <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full">
                          🔄 Restock
                        </span>
                      )}
                      {part.part_source === "Truck Part" && part.needs_replacement === false && (
                        <span className="ml-2 px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                          No restock
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="font-bold text-green-600">${part.calculated_price.toFixed(2)}</span>
                    <Button 
                      type="button" 
                      variant="ghost" 
                      size="sm"
                      onClick={() => removePart(index)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t flex justify-between items-center">
              <span className="text-lg font-semibold">Total:</span>
              <span className="text-2xl font-bold text-green-600">${getTotal().toFixed(2)}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit Button */}
      <Button 
        type="submit" 
        className="w-full h-12 text-lg"
        disabled={loading || parts.length === 0}
      >
        {loading ? 'Creating Orders...' : `Submit Order${parts.length > 1 ? ` (${parts.length} parts)` : ''}`}
      </Button>
    </form>
  );
}
